import React, {useState} from 'react';
import {Alert, Box, Button, Container, Grid, Paper, Snackbar, Stack, Typography} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {useNavigate} from "react-router";
import {useMyInstructorProfile} from "./useMyInstructorProfile";
import {Form} from "../form/Form";
import {FormProvider} from "../form/formContext";
import {InstructorForm} from "./InstructorForm";
import * as Yup from "yup";
import {InstructorMap} from "./InstructorMap";
import {FormSubmit} from "../form/FormSubmit";
import {InitializeForm} from "../form/InitializeForm";
import {PublishButton} from "./PublishButton";
import {InstructorCoursesManager} from "../instructors/InstructorCoursesManager";

export const InstructorProfile = ({userId}) => {
    const navigate = useNavigate();
    const {
        instructor,
        saveInstructor,
        publishInstructor,
        saveUpcomingCourses,
    } = useMyInstructorProfile({
        userId: userId,
    });

    const [feedback, setFeedback] = useState([]);

    const onPublish = () => {
        publishInstructor()
            .then(() => {
                setFeedback(
                    f =>[
                        ...f,
                        {
                            severity: 'success',
                            message: "Profilen ble publisert"
                        }
                    ]
                )
            })
            .catch(error => {
                console.error({error})
                setFeedback(
                    f =>[
                        ...f,
                        {
                            severity: 'error',
                            message: "Noe gikk galt ved publisering"
                        }
                    ]
                )
            })

    }

    const onSubmit = formData => {
        saveInstructor({
            ...formData,
            id: instructor.id,
            owner: instructor.owner
        })
            .then(() => {
                    setFeedback(
                        f =>[
                            ...f,
                            {
                                severity: 'success',
                                message: "Profilen ble oppdatert"
                            }
                        ]
                    )
            })
            .catch(error => {
                console.error({error})
                    setFeedback(
                        f =>[
                            ...f,
                            {
                                severity: 'error',
                                message: "Noe gikk galt ved lagring"
                            }
                        ]
                    )
            })
    }

    const validationSchema = Yup.object().shape({
        name: Yup.string()
            .required('Skriv inn navnet ditt'),
        title: Yup.string()
            .required("Skriv inn din jobbtittel"),
        description: Yup.string(),
        email: Yup.string()
            .required('Skriv inn e-postadressen din'),
        phone: Yup.string(),
        address: Yup.string()
            .required("Skriv inn addressen kurset holdes på"),
        place: Yup.string()
            .required("Skriv inn stedet kurset holdes på."),
        county: Yup.string()
            .required("Velg kommunen kurset holdes på."),
        location: Yup.object().shape({
            lat: Yup.number(),
            lon: Yup.number()
        })
    })

    if(!instructor?.owner) {
        return null
    } else {
        return (
            <Container maxWidth="lg" sx={{ py: 3, px: {xs: 2, sm: 3} }}>
                <Box sx={{ mb: 3 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/me")}
                        sx={{
                            mb: 2,
                            color: "text.secondary",
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: 2,
                            minHeight: 44,
                            "&:hover": { color: "primary.main" },
                            "&:focus-visible": { outline: "2px solid #285238", outlineOffset: 2 },
                        }}
                    >
                        Tilbake til Min side
                    </Button>

                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{ fontWeight: 700, color: "primary.main", mb: 0.5 }}
                    >
                        Rediger kursholderprofil
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Her kan du oppdatere din kontaktinformasjon, adresse og kartposisjon som vises offentlig for deltakere.
                    </Typography>
                </Box>

                <FormProvider
                    onSubmit={onSubmit}
                    schema={validationSchema}
                >
                    <InitializeForm
                        initialValues={instructor}
                    />
                    <Paper
                        elevation={0}
                        sx={{
                            padding: {xs: 2.5, sm: 4},
                            mb: 3,
                            borderRadius: 3,
                            border: "1px solid rgba(40, 82, 56, 0.12)",
                            boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
                        }}
                    >
                    <Grid
                        container={true}
                        spacing={2}
                        sx={{
                            mb: 4
                        }}
                    >
                        <Grid
                            size={{
                                xs: 12,
                                md: 6
                            }}
                        >
                            <Form>
                                <InstructorForm />
                            </Form>
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6
                            }}
                        >
                            <InstructorMap
                                formKey={"location"}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 12
                            }}
                        >
                            <Stack
                                direction={"row"}
                                spacing={2}
                            >
                                <Box
                                    flexGrow={1}
                                />
                                <FormSubmit
                                    variant={"contained"}
                                    color={"primary"}
                                >
                                    Lagre
                                </FormSubmit>
                                <PublishButton
                                    published={instructor.published}
                                    newProfile={instructor.newProfile}
                                    onClick={onPublish}
                                />
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>
                <Snackbar
                    open={feedback.length > 0}
                    autoHideDuration={6000}
                    onClose={() => setFeedback([])}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'center'
                    }}
                >
                    <Alert severity={feedback[0]?.severity}>
                        {feedback[0]?.message}
                    </Alert>
                </Snackbar>
            </FormProvider>

            {/* Course Cohorts & Calendar Management */}
            {instructor.id && (
                <Paper
                    elevation={0}
                    sx={{
                        padding: {xs: 2.5, sm: 4},
                        mb: 4,
                        borderRadius: 3,
                        border: "1px solid rgba(40, 82, 56, 0.12)",
                        boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
                    }}
                >
                    <InstructorCoursesManager
                        courses={instructor.upcomingCourses || []}
                        onSaveCourses={saveUpcomingCourses}
                        defaultLocation={instructor.place ? `${instructor.place}${instructor.county ? `, ${instructor.county}` : ""}` : ""}
                    />
                </Paper>
            )}
        </Container>
    );
  }
};